/**
 * DemoPreview Component
 * 
 * Displays a looping pre-scripted demo showing the game interface with
 * animated messages, character switching, stage directions, etc.
 * Uses the SAME visual structure as the real game interface (Contacts + Messaging).
 * Rotates through multiple themed scenarios (horror, detective, romance, fantasy).
 * 
 * Features demonstrated:
 * - Pre-existing messages
 * - User sending a message
 * - Stage directions (didascalie)
 * - Character switching
 * - New character appearing
 * - Adding a new contact (+ button simulation)
 * - Image/resource in message
 * - Choice message
 * - Cliffhanger ending
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Icon, Button, Checkbox } from 'semantic-ui-react';
import './DemoPreview.css';

// Demo icon paths - place custom icons in public/icons/demo/
// If the icon file doesn't exist, the emoji fallback will be used
const DEMO_ICONS = {
    horror: {
        main: '/icons/demo/horror-main.webp',
        contacts: ['/icons/demo/horror-emma.webp', '/icons/demo/horror-mystery.webp'],
    },
    detective: {
        main: '/icons/demo/detective-main.webp',
        contacts: ['/icons/demo/detective-inspector.webp', '/icons/demo/detective-witness.webp'],
    },
    romance: {
        main: '/icons/demo/romance-main.webp',
        contacts: ['/icons/demo/romance-morgan.webp', '/icons/demo/romance-friend.webp'],
    },
    fantasy: {
        main: '/icons/demo/fantasy-main.webp',
        contacts: ['/icons/demo/fantasy-guild.webp', '/icons/demo/fantasy-dragon.webp'],
    },
};

// Demo scenarios - each shows different features
const DEMO_SCENARIOS = {
    horror: {
        theme: 'horror',
        themeIcon: '👻',
        themeColor: '#8b0000',
        startTime: '23:45',
        mainCharacter: { nameKey: 'demoMainCharacter' },
        contacts: [
            { id: '1', nameKey: 'demoContact1' },
            { id: '2', nameKey: 'demoContact2' },
        ],
        sequence: [
            // Pre-existing messages
            { type: 'message', from: '1', text: 'demoHorror1', delay: 0 },
            { type: 'message', from: 'user', text: 'demoHorror2', delay: 1500 },
            { type: 'message', from: '1', text: 'demoHorror3', delay: 4000 },
            // Stage direction
            { type: 'didascalie', text: 'demoHorrorDidascalie1', delay: 6000 },
            // User types and sends (typing is automatic)
            { type: 'message', from: 'user', text: 'demoHorror4', delay: 8000 },
            // Response with image
            { type: 'image', from: '1', text: 'demoHorrorImage', imageSrc: '/icons/demo/horror_attic_doll_01.webp', delay: 11000 },
            // Another stage direction
            { type: 'didascalie', text: 'demoHorrorDidascalie2', delay: 12000 },
            // New character appears via + button animation
            { type: 'addContactAnimation', contactId: '2', delay: 14000 },
            { type: 'newContact', contactId: '2', delay: 15500 },
            { type: 'switchContact', contactId: '2', delay: 16000 },
            // Cliffhanger message from mystery character
            { type: 'message', from: '2', text: 'demoHorrorCliffhanger', delay: 17000 },
            // End pause before loop
            { type: 'pause', delay: 21000 },
        ],
    },
    detective: {
        theme: 'detective',
        themeIcon: '🔍',
        themeColor: '#1a1a2e',
        startTime: '14:30',
        mainCharacter: { nameKey: 'demoMainCharacter' },
        contacts: [
            { id: '1', nameKey: 'demoContact1' },
            { id: '2', nameKey: 'demoContact2' },
        ],
        sequence: [
            { type: 'message', from: '1', text: 'demoDetective1', delay: 0 },
            { type: 'message', from: 'user', text: 'demoDetective2', delay: 2000 },
            { type: 'didascalie', text: 'demoDetectiveDidascalie1', delay: 4000 },
            { type: 'image', from: '1', text: 'demoDetective3', imageSrc: '/icons/demo/detective_polaroid_card_01.webp', delay: 6000 },
            { type: 'choice', text: 'demoDetectiveChoice', choices: ['demoDetectiveChoice1', 'demoDetectiveChoice2', 'demoDetectiveChoice3'], delay: 8000 },
            { type: 'selectChoice', choiceIndex: 1, delay: 10000 },
            { type: 'submitChoice', delay: 11500 },
            { type: 'message', from: '1', text: 'demoDetective5', delay: 13000 },
            { type: 'addContactAnimation', contactId: '2', delay: 15000 },
            { type: 'newContact', contactId: '2', delay: 16500 },
            { type: 'switchContact', contactId: '2', delay: 17000 },
            { type: 'message', from: '2', text: 'demoDetectiveCliffhanger', delay: 18000 },
            { type: 'pause', delay: 22000 },
        ],
    },
    romance: {
        theme: 'romance',
        themeIcon: '💕',
        themeColor: '#ff6b6b',
        startTime: '19:15',
        mainCharacter: { nameKey: 'demoMainCharacter' },
        contacts: [
            { id: '1', nameKey: 'demoContact1' },
            { id: '2', nameKey: 'demoContact2' },
        ],
        sequence: [
            { type: 'message', from: '1', text: 'demoRomance1', delay: 0 },
            { type: 'message', from: 'user', text: 'demoRomance2', delay: 2000 },
            { type: 'image', from: '1', text: 'demoRomanceImage', imageSrc: '/icons/demo/romance_keepsake_rose_01.webp', delay: 5000 },
            { type: 'didascalie', text: 'demoRomanceDidascalie1', delay: 7500 },
            { type: 'message', from: 'user', text: 'demoRomance4', delay: 9000 },
            { type: 'message', from: '1', text: 'demoRomance5', delay: 12000 },
            { type: 'addContactAnimation', contactId: '2', delay: 13500 },
            { type: 'newContact', contactId: '2', delay: 15000 },
            { type: 'switchContact', contactId: '2', delay: 15500 },
            { type: 'message', from: '2', text: 'demoRomanceCliffhanger', delay: 16500 },
            { type: 'pause', delay: 20500 },
        ],
    },
    fantasy: {
        theme: 'fantasy',
        themeIcon: '⚔️',
        themeColor: '#6a0dad',
        startTime: '06:00',
        mainCharacter: { nameKey: 'demoMainCharacter' },
        contacts: [
            { id: '1', nameKey: 'demoContact1' },
            { id: '2', nameKey: 'demoContact2' },
        ],
        sequence: [
            { type: 'message', from: '1', text: 'demoFantasy1', delay: 0 },
            { type: 'message', from: 'user', text: 'demoFantasy2', delay: 2000 },
            { type: 'didascalie', text: 'demoFantasyDidascalie1', delay: 4000 },
            // Choice for quest
            { type: 'choice', text: 'demoFantasyChoice', choices: ['demoFantasyChoice1', 'demoFantasyChoice2'], delay: 6000 },
            { type: 'selectChoice', choiceIndex: 0, delay: 8000 },
            { type: 'submitChoice', delay: 9500 },
            { type: 'message', from: '1', text: 'demoFantasy5', delay: 11000 },
            { type: 'image', from: '1', text: 'demoFantasyImage', imageSrc: '/icons/demo/fantasy_map_01.webp', delay: 13000 },
            { type: 'addContactAnimation', contactId: '2', delay: 15500 },
            { type: 'newContact', contactId: '2', delay: 17000 },
            { type: 'switchContact', contactId: '2', delay: 17500 },
            { type: 'message', from: '2', text: 'demoFantasyCliffhanger', delay: 18500 },
            { type: 'pause', delay: 22500 },
        ],
    },
};

const SCENARIO_ORDER = ['detective', 'horror', 'romance', 'fantasy'];

// Preload all demo images on module load (icons + inline message images)
const preloadAllDemoImages = () => {
    // preload icons
    Object.values(DEMO_ICONS).forEach(iconSet => {
        const mainImg = new Image();
        mainImg.src = iconSet.main;
        iconSet.contacts.forEach(contactIcon => {
            const img = new Image();
            img.src = contactIcon;
        });
    });

    // preload images referenced in demo sequences
    Object.values(DEMO_SCENARIOS).forEach(scenario => {
        scenario.sequence.forEach(step => {
            if (step.type === 'image' && step.imageSrc) {
                const img = new Image();
                img.src = step.imageSrc;
            }
        });
    });
};
preloadAllDemoImages();

export default function DemoPreview({ autoPlay = true }) {
    const { t } = useTranslation();
    const [currentScenarioIndex, setCurrentScenarioIndex] = useState(0);
    const [messages, setMessages] = useState([]);
    const [visibleContacts, setVisibleContacts] = useState([]);
    const [activeContactId, setActiveContactId] = useState('1');
    const [isTyping, setIsTyping] = useState(false);
    const [typingText, setTypingText] = useState('');
    const [currentTime, setCurrentTime] = useState('');
    const [addButtonAnimating, setAddButtonAnimating] = useState(false);
    const [choiceData, setChoiceData] = useState(null); // { choices: [], selectedIndex: null }
    const messagesEndRef = useRef(null);
    const messagesContainerRef = useRef(null);
    const demoRootRef = useRef(null);
    const timeoutsRef = useRef([]);

    const currentTheme = SCENARIO_ORDER[currentScenarioIndex];
    const currentScenario = DEMO_SCENARIOS[currentTheme];
    const iconSet = DEMO_ICONS[currentTheme];

    // Get avatar for a contact
    const getContactAvatar = useCallback((contact, index) => {
        return { type: 'image', src: iconSet.contacts[index] };
    }, [iconSet]);

    // Get main character avatar
    const getMainAvatar = useCallback(() => {
        return { type: 'image', src: iconSet.main };
    }, [iconSet]);

    // Clear all timeouts
    const clearAllTimeouts = useCallback(() => {
        timeoutsRef.current.forEach(id => {
            clearTimeout(id);
            clearInterval(id);
        });
        timeoutsRef.current = [];
    }, []);

    // Reset demo state
    const resetDemo = useCallback(() => {
        clearAllTimeouts();
        setMessages([]);
        setVisibleContacts([currentScenario.contacts[0]]);
        setActiveContactId('1');
        setIsTyping(false);
        setTypingText('');
        setCurrentTime(currentScenario.startTime);
        setAddButtonAnimating(false);
        setChoiceData(null);
    }, [currentScenario, clearAllTimeouts]);

    // Run demo sequence
    const runSequence = useCallback(() => {
        resetDemo();

        // Simulate time passing
        let timeMinutes = parseInt(currentScenario.startTime.split(':')[1]);
        let timeHours = parseInt(currentScenario.startTime.split(':')[0]);
        const timeInterval = setInterval(() => {
            timeMinutes += 1;
            if (timeMinutes >= 60) {
                timeMinutes = 0;
                timeHours = (timeHours + 1) % 24;
            }
            setCurrentTime(`${timeHours.toString().padStart(2, '0')}:${timeMinutes.toString().padStart(2, '0')}`);
        }, 3000);
        timeoutsRef.current.push(timeInterval);

        currentScenario.sequence.forEach((step, index) => {
            const timeout = setTimeout(() => {
                switch (step.type) {
                    case 'message':
                        if (step.from === 'user') {
                            // For user messages, show typing animation first then send
                            setIsTyping(true);
                            const userText = t(`demo.${currentScenario.theme}.${step.text}`);
                            let charIdx = 0;
                            const userTypeInterval = setInterval(() => {
                                if (charIdx < userText.length) {
                                    setTypingText(userText.slice(0, charIdx + 1));
                                    charIdx++;
                                } else {
                                    clearInterval(userTypeInterval);
                                    // Short delay then "send" the message
                                    const sendTimeout = setTimeout(() => {
                                        setIsTyping(false);
                                        setTypingText('');
                                        setMessages(prev => [...prev, {
                                            id: `msg-${index}`,
                                            from: step.from,
                                            text: userText,
                                            isUser: true,
                                        }]);
                                    }, 200);
                                    timeoutsRef.current.push(sendTimeout);
                                }
                            }, 40);
                            timeoutsRef.current.push(userTypeInterval);
                        } else {
                            // Non-user messages appear directly
                            setMessages(prev => [...prev, {
                                id: `msg-${index}`,
                                from: step.from,
                                text: t(`demo.${currentScenario.theme}.${step.text}`),
                                isUser: false,
                            }]);
                        }
                        break;
                    
                    case 'image':
                        setMessages(prev => [...prev, {
                            id: `img-${index}`,
                            from: step.from,
                            text: t(`demo.${currentScenario.theme}.${step.text}`),
                            isUser: step.from === 'user',
                            hasImage: true,
                            imageSrc: step.imageSrc || null,
                            imagePlaceholder: step.imagePlaceholder || null,
                        }]);
                        break;
                    
                    case 'didascalie':
                        setMessages(prev => [...prev, {
                            id: `did-${index}`,
                            type: 'didascalie',
                            text: t(`demo.${currentScenario.theme}.${step.text}`),
                        }]);
                        break;
                    
                    case 'choice':
                        setChoiceData({
                            id: `choice-${index}`,
                            text: t(`demo.${currentScenario.theme}.${step.text}`),
                            choices: step.choices.map(c => t(`demo.${currentScenario.theme}.${c}`)),
                            selectedIndex: null,
                        });
                        break;
                    
                    case 'selectChoice':
                        setChoiceData(prev => prev ? { ...prev, selectedIndex: step.choiceIndex } : null);
                        break;
                    
                    case 'submitChoice':
                        // Add the selected choice as a user message
                        setChoiceData(prev => {
                            if (prev && prev.selectedIndex !== null) {
                                setMessages(msgs => [...msgs, {
                                    id: `choice-submit-${index}`,
                                    from: 'user',
                                    text: prev.choices[prev.selectedIndex],
                                    isUser: true,
                                }]);
                            }
                            return null; // Clear choice data
                        });
                        break;
                    
                    case 'addContactAnimation':
                        setAddButtonAnimating(true);
                        // Stop animation after 1.5s
                        const stopAnimTimeout = setTimeout(() => {
                            setAddButtonAnimating(false);
                        }, 1500);
                        timeoutsRef.current.push(stopAnimTimeout);
                        break;
                    
                    case 'newContact':
                        const newContact = currentScenario.contacts.find(c => c.id === step.contactId);
                        if (newContact) {
                            setVisibleContacts(prev => [...prev, newContact]);
                        }
                        break;
                    
                    case 'switchContact':
                        setActiveContactId(step.contactId);
                        setMessages([]); // Clear messages when switching
                        setChoiceData(null);
                        break;
                    
                    case 'pause':
                        // After pause, move to next scenario
                        const nextTimeout = setTimeout(() => {
                            setCurrentScenarioIndex(prev => 
                                (prev + 1) % SCENARIO_ORDER.length
                            );
                        }, 2000);
                        timeoutsRef.current.push(nextTimeout);
                        break;
                }
            }, step.delay);
            
            timeoutsRef.current.push(timeout);
        });
    }, [currentScenario, t, resetDemo]);

    // Start/restart sequence when scenario changes
    useEffect(() => {
        if (autoPlay) {
            runSequence();
        }
        return () => clearAllTimeouts();
    }, [currentScenarioIndex, autoPlay, runSequence, clearAllTimeouts]);

    // Auto-scroll messages within the container only (not the whole page)
    useEffect(() => {
        if (messagesEndRef.current) {
            const container = messagesEndRef.current.closest('.demo-messages-inner');
            if (container) {
                // Use requestAnimationFrame to ensure DOM is ready
                requestAnimationFrame(() => {
                    container.scrollTo({
                        top: container.scrollHeight,
                        behavior: 'smooth'
                    });
                });
            }
        }
    }, [messages, choiceData]);

    // Keep CSS variable for messages container height so images can be sized relative to it
    useEffect(() => {
        const updateSizes = () => {
            const container = messagesContainerRef.current;
            const root = demoRootRef.current;
            if (container && root) {
                const h = container.clientHeight;
                root.style.setProperty('--demo-messages-height', `${h}px`);
            }
        };

        updateSizes();
        window.addEventListener('resize', updateSizes);
        return () => window.removeEventListener('resize', updateSizes);
    }, [messages]);

    const activeContact = currentScenario.contacts.find(c => c.id === activeContactId) 
        || currentScenario.contacts[0];
    const activeContactIndex = currentScenario.contacts.findIndex(c => c.id === activeContactId);
    const activeContactAvatar = getContactAvatar(activeContact, activeContactIndex);
    const mainCharacterAvatar = getMainAvatar();

    // Helper to get translated name for contacts and characters
    const getTranslatedName = (item) => {
        if (item.nameKey) {
            return t(`demo.${currentScenario.theme}.${item.nameKey}`);
        }
        return item.name || '';
    };

    // Helper to render avatar image
    const renderAvatar = (avatar, className = '') => {
        return <img src={avatar.src} alt="" className={`demo-avatar-img ${className}`} />;
    };

    // Format date for display
    const today = new Date();
    const formattedDate = `${today.getDate().toString().padStart(2, '0')}/${(today.getMonth() + 1).toString().padStart(2, '0')}`;

    return (
        <div ref={demoRootRef} className="demo-preview" style={{ '--theme-color': currentScenario.themeColor }}>
            {/* Theme indicator */}
            <div className="demo-theme-badge">
                <span className="demo-theme-icon">{currentScenario.themeIcon}</span>
                <span className="demo-theme-name">{t(`demo.themes.${currentScenario.theme}`)}</span>
            </div>

            {/* Game Interface Container - mimics real GameInterface layout */}
            <div className="demo-game-interface">
                {/* Contacts Sidebar - like real Contacts component */}
                <div className="demo-contacts-sidebar">
                    {/* Contact List */}
                    <div className="demo-contact-list">
                        {visibleContacts.map((contact) => {
                            const contactIndex = currentScenario.contacts.indexOf(contact);
                            const avatar = getContactAvatar(contact, contactIndex);
                            return (
                                <div 
                                    key={contact.id}
                                    className={`demo-contact-item ${contact.id === activeContactId ? 'active' : ''}`}
                                >
                                    <div className="demo-contact-avatar-wrapper">
                                        {renderAvatar(avatar, 'demo-contact-emoji')}
                                    </div>
                                    <div className="demo-contact-info">
                                        <span className="demo-contact-name-text">{getTranslatedName(contact)}</span>
                                    </div>
                                    {contact.id !== activeContactId && (
                                        <span className="demo-unread-dot" />
                                    )}
                                </div>
                            );
                        })}
                    </div>

                    {/* Add Contact Input - like real ContactSidebar */}
                    <div className={`demo-new-contact-section ${addButtonAnimating ? 'animating' : ''}`}>
                        <div className="demo-new-contact-input">
                            <input 
                                type="text" 
                                placeholder={t('PlaceholderNumberIdentifier')}
                                readOnly
                                className="demo-identifier-input"
                            />
                            <Button icon className="demo-add-contact-button">
                                <Icon name="add" />
                            </Button>
                        </div>
                    </div>
                </div>

                {/* Messaging Area - like real ChatWindow component */}
                <div 
                    className="demo-messaging-container" 
                    style={{ 
                        '--bg-emoji': activeContactAvatar.type === 'emoji' ? `"${activeContactAvatar.src}"` : 'none',
                        '--bg-image': activeContactAvatar.type === 'image' ? `url(${activeContactAvatar.src})` : 'none'
                    }}
                >
                    {/* Background overlay */}
                    <div className={`demo-messaging-bg ${activeContactAvatar.type === 'image' ? 'has-image' : ''}`}></div>
                    {/* Information Bar - mimics InformationBar.jsx with 3 columns */}
                    <div className="demo-information-bar">
                        {/* Active Character (left) */}
                        <div className="demo-active-character">
                            <div className="demo-character-image">
                                {renderAvatar(activeContactAvatar)}
                            </div>
                            <div className="demo-character-text">
                                <span className="demo-character-name">{getTranslatedName(activeContact)}</span>
                            </div>
                        </div>

                        {/* Date/Time (center) */}
                        <div className="demo-date-info">
                            <div className="demo-date-info-time">{currentTime}</div>
                            <div className="demo-date-info-date">{formattedDate}</div>
                        </div>

                        {/* Main Character (right) */}
                        <div className="demo-main-character-info">
                            <div className="demo-character-text">
                                <i className="demo-main-character-hook">{t('You play')}<br/></i>
                                <span className="demo-character-name">{getTranslatedName(currentScenario.mainCharacter)}</span>
                            </div>
                            <div className="demo-character-image">
                                {renderAvatar(mainCharacterAvatar)}
                            </div>
                        </div>
                    </div>

                    {/* Messages Container */}
                    <div ref={messagesContainerRef} className="demo-messages-container">
                        <div className="demo-messages-inner">
                            {messages.map(msg => (
                                <div 
                                    key={msg.id}
                                    className={`demo-message-wrapper ${
                                        msg.type === 'didascalie' 
                                            ? 'demo-scene-description' 
                                            : msg.isUser 
                                                ? 'demo-player' 
                                                : 'demo-ordi'
                                    }`}
                                >
                                    <div className="demo-message-bubble">
                                        {msg.hasImage && (
                                            <div className="demo-message-image">
                                                {msg.imageSrc ? (
                                                    <img src={msg.imageSrc} alt={msg.text} className="demo-image-asset" />
                                                ) : (
                                                    <span className="demo-image-placeholder">{msg.imagePlaceholder}</span>
                                                )}
                                            </div>
                                        )}
                                        <p>{msg.text}</p>
                                    </div>
                                </div>
                            ))}
                            
                            {/* Choice display */}
                            {choiceData && (
                                <div className="demo-choice-container">
                                    {choiceData.text && <p className="demo-choice-question">{choiceData.text}</p>}
                                    <div className="demo-choice-options">
                                        {choiceData.choices.map((choice, idx) => (
                                            <div key={idx} className="demo-choice-option">
                                                <Checkbox 
                                                    label={choice}
                                                    checked={choiceData.selectedIndex === idx}
                                                    readOnly
                                                />
                                            </div>
                                        ))}
                                    </div>
                                    <Button 
                                        className="demo-choice-submit"
                                        disabled={choiceData.selectedIndex === null}
                                    >
                                        {t('demo.submitChoice')}
                                    </Button>
                                </div>
                            )}
                            
                            <div ref={messagesEndRef} />
                        </div>
                    </div>

                    {/* Input Area - Send Box */}
                    <div className="demo-send-box">
                        <input 
                            type="text" 
                            className="demo-message-input"
                            value={isTyping ? typingText : ''}
                            placeholder={t('demo.inputPlaceholder')}
                            readOnly
                        />
                        <Button className="demo-send-button" primary>
                            <Icon name="send" />
                        </Button>
                    </div>
                </div>
            </div>

            {/* Scenario dots */}
            <div className="demo-scenario-dots">
                {SCENARIO_ORDER.map((scenario, index) => (
                    <button
                        key={scenario}
                        className={`demo-dot ${index === currentScenarioIndex ? 'active' : ''}`}
                        onClick={() => setCurrentScenarioIndex(index)}
                        aria-label={t(`demo.themes.${scenario}`)}
                    >
                        <span>{DEMO_SCENARIOS[scenario].themeIcon}</span>
                    </button>
                ))}
            </div>
        </div>
    );
}
